<template>
  <div class="nav-container">
    <div v-if="isActive" class="nav-mobile">
      <div
        class="nav-mobile-close"
        @click="toggleMenuLinks()"
        v-show="isActive"
      >
        <span class="material-symbols-outlined close-icon">cancel</span>
      </div>
      <div class="nav-links" :class="{ active: isActive }" v-if="!isSimpleView">
        <div class="alerts-button-container">
          <GetDealAlerts />
        </div>
        <router-link to="/"><span @click="toggleMenuLinks(false)">Deals</span></router-link>
        <router-link to="/outlet" v-if="isOutletEnabled"
          ><span @click="toggleMenuLinks(false)">Outlet</span></router-link
        >
        <router-link to="/advanced-amazon-search"
          ><span @click="toggleMenuLinks(false)"
            >Amazon Search</span
          ></router-link
        >
        <router-link v-if="settings.showBlog" to="/blog"
          ><span @click="toggleMenuLinks(false)">Blog</span></router-link
        >
        <router-link v-if="settings.showLists" to="/lists"
          ><span @click="toggleMenuLinks(false)">Lists</span></router-link
        >
        <router-link v-if="settings.showGiftIdeas" to="/gift-ideas"
          ><span @click="toggleMenuLinks(false)">Gift Ideas</span></router-link
        >
        <router-link to="/about"
          ><span @click="toggleMenuLinks(false)">About</span></router-link
        >
        <router-link to="/contact"
          ><span @click="toggleMenuLinks(false)">Contact</span></router-link
        >
      </div>
    </div>
    <div
      id="nav-section"
      :class="{ 'focused-menu': $globalStore.isFocusedOnInput }"
    >
      <div class="nav-section-left">
        <router-link to="/">
          <img class="nav-section-logo" :src="settings.logoSquare" v-if="isSearchEnabled && settings.logoSquare" />
          <img class="nav-section-logo" :src="settings.logoUrl" v-else />
        </router-link>
      </div>
      <div class="nav-search-container" v-if="isSearchEnabled">
        <!--INPUT SEARCH BOX with search icon on the right-->
        <!--when they click enter or the search icon, it should call the search function-->
        <!--on focus we should show a clear button-->
        <input
          v-model="$globalStore.searchQuery"
          @input="searchSuggestionsDebounced"
          type="text"
          placeholder="Search deals, coupons, gift ideas, and more..."
          @focus="showSearchResults"
          @keyup.enter="search"
          @keyup.esc="hideSearchResults"
          class="nav-search-input"
        />

        <div
          class="nav-search-icon"
          @click="search"
          v-if="!$globalStore.isFocusedOnInput"
        >
          <span class="material-symbols-outlined">search</span>
        </div>
        <div
          class="nav-search-icon"
          @click="hideSearchResults"
          v-if="$globalStore.isFocusedOnInput"
        >
          <span class="material-symbols-outlined">close</span>
        </div>
      </div>
      <div v-else></div>
      <div
        class="nav-section-right cursor-pointer"
        @click="toggleMenuLinks()"
        v-if="!isSimpleView"
        :class="{ 'hidden-menu': $globalStore.isFocusedOnInput }"
      >
        <div class="material-symbols-outlined icon">menu</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import settings from "@/settings";
import VideoContainer2 from "@/components/VideoContainer2.vue";
import { globalStore } from "@/stores/globalStore";
import api from "@/api";
import util from "@/helpers/util";
import GetDealAlerts from "@/components/GetDealAlerts.vue";

const route = useRoute();
const router = useRouter();
const isActive = ref(false);
const isSimpleView = ref(false);
const $globalStore = globalStore();
const isSearchEnabled = ref(false);
const isOutletEnabled = ref(false);

const showSearchResults = () => {
  $globalStore.setFocusedOnInput(true);
};

const hideSearchResults = () => {
  $globalStore.setFocusedOnInput(false);

  //unfocus from current input
  (document.activeElement as HTMLInputElement)?.blur();
};

onMounted(() => {
  isSimpleView.value = !!route.query.direct;
});

onUnmounted(() => {
  document.body.classList.remove("no-scroll");
});

const toggleMenuLinks = (toggleValue: boolean | null = null) => {
  if (toggleValue === null) {
    isActive.value = !isActive.value;
  } else {
    isActive.value = toggleValue;
  }

  if (isActive.value) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.body.classList.add("no-scroll");
  } else {
    document.body.classList.remove("no-scroll");
  }
};

const search = () => {
  if ($globalStore.searchQuery.length > 0) {
    (document.activeElement as HTMLInputElement)?.blur();
    $globalStore.setFocusedOnInput(false);
    $globalStore.clearCategoryNodes();
    router.push({
      name: "search-results",
      query: {
        q: $globalStore.searchQuery,
        price: $globalStore.maxPrice,
        percent: $globalStore.minPercentOff,
      },
    });
  }
};

const searchSuggestionsDebounced = () => {
  util.debounce(async () => {
    if ($globalStore.searchQuery.length == 0) {
      $globalStore.setSearchSuggestions([]);
      return;
    }

    if ($globalStore.searchQuery) {
      const response = await api.getSearchSuggestions($globalStore.searchQuery);
      $globalStore.setSearchSuggestions(response.data);
    }
  }, 500);
};
</script>

<style scoped>
.hide-search-results {
  display: none;
}

div#nav-section {
  display: grid;
  grid-template-columns: 60px 1fr 50px;
  border-bottom: 1px solid #cecece;
  gap: 5px;
  align-items: center;
}

.nav-search-container {
  width: 100%;
  height: 40px;
  display: grid;
  grid-template-columns: 1fr 20px;
  align-items: center;
  justify-content: center;
}

.nav-search-input {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 10px;
  padding-right: 32px;
}

.nav-search-input:focus {
  outline: none;
}

.nav-search-icon {
  height: 37px;
  margin-left: -20px;
  background-color: #19b355;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hidden-menu {
  display: none;
}

div#nav-section.focused-menu {
  grid-template-columns: 60px 1fr;
  padding-right: 10px;
}

.close-icon {
  color: black;
  font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48;
  font-size: 50px;
}

.alerts-button-container {
  margin-bottom: 10px;
}
</style>
